import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import Http from "../../Http";
import { Insurance_API } from "../../Helper";
// Import Icon from lucide-react
import { ChevronDown } from "lucide-react";
import FAQSchema from "../../FAQSchema";
import UniversalInsuranceLoader from "../Loading/UniversalInsuranceLoader";

function Icon({ id, open }) {
  return (
    <ChevronDown
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    />
  );
}

const CarInsuranceDetail = () => {
  const { pageUrl } = useParams();
  const [insuranceDetail, setInsuranceDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [activeIndex, setActiveIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(
    insuranceDetail?.insurance_faq?.length - 1 || 0
  );
  useEffect(() => {
    fetchInsuranceDetail();
  }, [pageUrl]);

  const fetchInsuranceDetail = async () => {
    try {
      const response = await Http.get(Insurance_API);

      // Check if the API returns data in the expected format
      const insuranceArray = response.insurance || response;

      if (!Array.isArray(insuranceArray)) {
        throw new Error("Invalid data format received from API");
      }

      // const detail = insuranceArray.find(item => item.page_url === pageUrl);
      // Filter by both page_url AND the category name
      const detail = insuranceArray.find(
        (item) => item.page_url === pageUrl && item.category_name === "Car" // This ensures we only get Car insurance items
      );
      if (detail) {
        setInsuranceDetail(detail);
      } else {
        setError("Insurance detail not found");
      }
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch insurance detail: " + error.message);
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      setActiveIndex(null);
    } else {
      setActiveIndex(index);
    }
  };

  if (loading) {
    return (
      <UniversalInsuranceLoader
        type="car"
        title="Loading Car Insurance Details"
        subtitle="Finding the best coverage options for your vehicle..."
        theme="blue"
      />
    );
  }
  if (loading)
    return (
      <div className="text-center py-10">Loading insurance details...</div>
    );
  if (error)
    return <div className="text-center py-10 text-red-600">{error}</div>;
  if (!insuranceDetail)
    return <div className="text-center py-10">Insurance details not found</div>;

  return (
    <>
      <Helmet>
        <title>{insuranceDetail.metatitle || insuranceDetail.page_title}</title>
        <meta
          name="description"
          content={insuranceDetail.metadesc}
        />
        <meta name="keywords" content={insuranceDetail.metakeyword} />
        <link
          rel="canonical"
          href={`https://thefinmaster.com/car-insurance/${pageUrl}`}
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <FAQSchema faqs={insuranceDetail.insurance_faq} />

      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/car-insurance"
              className="text-blue-600 hover:text-blue-800 "
            >
              Car Insurance
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">{insuranceDetail.page_title}</span>
          </li>
        </ol>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8 mb-8">
          <div className="md:w-1/3">
            <img
              src={insuranceDetail.image}
              alt={insuranceDetail.page_title}
              className="w-full rounded-lg shadow-lg"
            />
          </div>
          <div className="md:w-2/3">
            <h1 className="text-3xl font-bold mb-4">
              {insuranceDetail.page_title}
            </h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-1">Premium Starting</p>
                <p className="text-2xl font-bold">
                  ₹{insuranceDetail.premium_start}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-1">Claim Settlement</p>
                <p className="text-2xl font-bold">
                  {insuranceDetail.claim_settlement}%
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-gray-600 mb-1">Network Garages</p>
                <p className="text-2xl font-bold">
                  {insuranceDetail.network_garages}+
                </p>
              </div>
            </div>

            <a
              href={insuranceDetail.bank_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <button className="bg-green-500 text-white font-semibold py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300 mr-4">
                Get Quote Now
              </button>
            </a>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-6">
            About {insuranceDetail.page_title}
          </h2>
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: insuranceDetail.page_details }}
          />
        </div>

        {insuranceDetail.insurance_faq &&
          insuranceDetail.insurance_faq.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold mb-6 text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {insuranceDetail.insurance_faq.map((faq, index) => (
                  <div
                    key={index}
                    className="border rounded-lg overflow-hidden shadow-md"
                  >
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                    >
                      {faq.question}
                      <Icon id={index} open={activeIndex} />
                    </button>
                    {activeIndex === index && (
                      <div className="p-4 bg-gray-100">
                        <p className="text-gray-700">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    </>
  );
};

export default CarInsuranceDetail;
